import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { produce } from 'immer';
import { tap, catchError } from 'rxjs/operators';

import { PosManagerService } from 'src/app/services';
import {
  POS_REPORTS_STATE_TOKEN,
  PosReportsStateModel
} from './pos-reports.model';
import {
  GetEndOfDayReport,
  SendPdfReport,
  SendPdfReportSuccess,
  SendPdfReportError,
  PrintReport,
  PrintReportError,
  GetReportRecipients,
  GetReportRecipientsSuccess,
  GetReportRecipientsError
} from './pos-reports.action';

@State({
  name: POS_REPORTS_STATE_TOKEN,
  defaults: {
    endOfDayReport: {
      isLoaded: false,
      isProcessing: false,
      data: null
    },
    reportRecipients: {
      isLoading: false,
      data: null,
      error: null
    }
  }
})
@Injectable()
export class PosReportsState {
  constructor(private readonly posManagerService: PosManagerService) {}

  @Action(GetEndOfDayReport)
  getEndOfDayReport(
    { setState }: StateContext<PosReportsStateModel>,
    { payload }: GetEndOfDayReport
  ) {
    setState(
      produce((draft) => {
        draft.endOfDayReport.isProcessing = true;
        draft.endOfDayReport.isLoaded = false;
        draft.endOfDayReport.data = null;
      })
    );

    return this.posManagerService
      .getEndOfDayReport({
        ...payload,
        end_day_report: true // ensure this flag is set
      })
      .pipe(
        tap({
          next: (response) =>
            setState(
              produce((draft) => {
                if (response) {
                  draft.endOfDayReport.isLoaded = true;
                  draft.endOfDayReport.data = response;
                }
              })
            ),
          finalize: () => {
            setState(
              produce((draft) => {
                draft.endOfDayReport.isProcessing = false;
              })
            );
          }
        })
      );
  }

  @Action(SendPdfReport)
  sendPdfReport(
    { dispatch }: StateContext<PosReportsStateModel>,
    { request }: SendPdfReport
  ) {
    return this.posManagerService.sendPdfReport(request).pipe(
      tap((response) => dispatch(new SendPdfReportSuccess(response.message))),
      catchError((error) => dispatch(new SendPdfReportError(error)))
    );
  }

  @Action(PrintReport)
  printReport(
    { dispatch }: StateContext<PosReportsStateModel>,
    { request }: PrintReport
  ) {
    return this.posManagerService
      .printReport(request)
      .pipe(catchError((error) => dispatch(new PrintReportError(error))));
  }

  @Action(GetReportRecipients)
  getReportRecipients({
    setState,
    dispatch
  }: StateContext<PosReportsStateModel>) {
    setState(
      produce((draft) => {
        draft.reportRecipients.isLoading = true;
        draft.reportRecipients.error = null;
      })
    );

    return this.posManagerService.getReportRecipients().pipe(
      tap((recipients) => dispatch(new GetReportRecipientsSuccess(recipients))),
      catchError((error) => dispatch(new GetReportRecipientsError(error)))
    );
  }

  @Action(GetReportRecipientsSuccess)
  getReportRecipientsSuccess(
    { setState }: StateContext<PosReportsStateModel>,
    { recipients }: GetReportRecipientsSuccess
  ) {
    setState(
      produce((draft) => {
        draft.reportRecipients.isLoading = false;
        draft.reportRecipients.data = recipients;
      })
    );
  }

  @Action(GetReportRecipientsError)
  getReportRecipientsError(
    { setState }: StateContext<PosReportsStateModel>,
    { error }: GetReportRecipientsError
  ) {
    setState(
      produce((draft) => {
        draft.reportRecipients.isLoading = false;
        draft.reportRecipients.error = error;
      })
    );
  }
}
